import { DecisionSystem } from '../ai/DecisionSystem';
import { ElementSystem } from '../ai/ElementSystem';
import { AnchorPolicy } from '../ai/AnchorPolicy';
import { MovementSystem } from '../ai/MovementSystem';
import { SpatialTelemetry } from '../ai/SpatialTelemetry';
import { TacticalAI } from '../ai/TacticalAI';
import { normaliseAssignedRole, validateAssignedRole } from '../ai/TacticalRoleSystem';
import { initialTeamAIState } from '../ai/TeamAIDirector';
import { DEFAULT_MATCH_ENGINE_CONFIG, type EnginePlayer, type EngineTeam, type EngineTechnique, type MatchEngineConfig } from '../data/EngineData';
import { DecisionLogger } from '../logs/DecisionLogger';
import { ActionResolver } from './ActionResolver';
import { FurySystem } from './FurySystem';
import { MatchClock } from './MatchClock';
import type { EngineMatchEvent } from './MatchEvent';
import { MatchRng } from './MatchRng';
import type { EngineMatchSnapshot, MatchState } from './MatchState';
import type { PlayerRuntimeState } from './PlayerRuntimeState';
import { PITCH_GEOMETRY } from './PitchGeometry';
import { arrangeKickoff } from './KickoffSetup';
import { distance, type Vector2 } from './Vector2';

export const SIMULATION_VERSION = 'utility-ai-3.2.1';

export interface MatchSetup {
  home: EngineTeam;
  away: EngineTeam;
  techniques: ReadonlyMap<string, EngineTechnique>;
  matchId?: string;
}

export interface EngineMatchResult {
  simulationVersion: string;
  state: MatchState;
  homeScore: number;
  awayScore: number;
  events: EngineMatchEvent[];
  snapshots: EngineMatchSnapshot[];
}

export interface MatchEngineOptions {
  enableDecisionLogging?: boolean;
  recordSnapshots?: boolean;
  autoFurySides?: readonly (0 | 1)[];
  /** Spatial metrics do not affect decisions. Headless batches can sample
   * them less often while preserving the full simulated match. */
  spatialTelemetryIntervalSeconds?: number;
}

export class MatchEngine {
  readonly state: MatchState;
  readonly rng: MatchRng;
  private readonly clock: MatchClock;
  private readonly anchors = new AnchorPolicy();
  private readonly movement = new MovementSystem();
  private readonly spatialTelemetry = new SpatialTelemetry();
  private readonly tactical = new TacticalAI();
  private readonly decisions = new DecisionSystem();
  private readonly logger: DecisionLogger;
  private readonly resolver: ActionResolver;
  private readonly fury = new FurySystem();
  private readonly techniques: ReadonlyMap<string, EngineTechnique>;
  private readonly recordSnapshots: boolean;
  private readonly autoFurySides: readonly (0 | 1)[];
  private readonly spatialTelemetryIntervalSeconds: number;
  private spatialTelemetryAccumulator = 0;
  private started = false;
  /** Runtime-only watchdog; it never changes save data or match determinism. */
  private playProgress = { position: { ...PITCH_GEOMETRY.centre }, ownerId: undefined as string | undefined, ballState: 'OWNED', at: 0 };

  constructor(
    setup: MatchSetup,
    seed: number,
    config: Partial<MatchEngineConfig> = {},
    options: MatchEngineOptions = {},
  ) {
    const resolvedConfig = { ...DEFAULT_MATCH_ENGINE_CONFIG, ...config };
    this.techniques = setup.techniques;
    this.rng = new MatchRng(seed);
    this.logger = new DecisionLogger(options.enableDecisionLogging ?? true);
    this.recordSnapshots = options.recordSnapshots ?? true;
    this.autoFurySides = options.autoFurySides ?? [0, 1];
    this.spatialTelemetryIntervalSeconds = Math.max(
      resolvedConfig.movementStepSeconds,
      options.spatialTelemetryIntervalSeconds ?? resolvedConfig.movementStepSeconds,
    );
    this.resolver = new ActionResolver(new ElementSystem(
      resolvedConfig.elementAdvantage,
      resolvedConfig.elementNeutral,
      resolvedConfig.elementDisadvantage,
    ), this.logger);
    this.clock = new MatchClock(
      resolvedConfig.durationSeconds,
      resolvedConfig.decisionIntervalSeconds,
      resolvedConfig.snapshotIntervalSeconds,
    );
    const teams: [EngineTeam, EngineTeam] = [setup.home, setup.away];
    const players = teams.flatMap((team) => this.createRuntimePlayers(team));
    const kickoffSide = this.rng.chance(0.5) ? 0 : 1;
    const kickoffPlayer = arrangeKickoff(players, kickoffSide);
    kickoffPlayer.hasBall = true;
    this.state = {
      matchId: setup.matchId ?? `match-${seed}`,
      seed,
      time: 0,
      config: resolvedConfig,
      teams,
      players,
      ball: {
        position: { ...PITCH_GEOMETRY.centre }, velocity: { x: 0, y: 0 }, ownerPlayerId: kickoffPlayer.playerId,
        state: 'OWNED', targetPosition: { ...PITCH_GEOMETRY.centre }, speed: 0, height: 0,
      },
      possessionTeam: kickoffSide,
      previousPossessionTeam: kickoffSide,
      lastPossessionChangeTime: -Infinity,
      counterWindowByTeam: [-Infinity, -Infinity],
      score: [0, 0],
      lastGoalConcededAtByTeam: [-Infinity, -Infinity],
      momentum: [0, 0],
      teamAi: [initialTeamAIState(0), initialTeamAIState(1)],
      fury: [{ used: false, active: false }, { used: false, active: false }],
      lastShotTime: [-Infinity, -Infinity],
      lastTackleTime: [-Infinity, -Infinity],
      lastTechniqueTime: [-Infinity, -Infinity],
      recentActions: [[], []],
      stats: {
        possessionSeconds: [0, 0], shots: [0, 0], shotsOnTarget: [0, 0], passes: [0, 0], techniques: [0, 0],
        turnovers: 0, deflections: 0, rebounds: 0, foulsCommitted: [0, 0], offsides: [0, 0],
        restarts: {
          throwIns: [0, 0], corners: [0, 0], goalKicks: [0, 0], freeKicks: [0, 0], penalties: [0, 0],
        },
      },
      spatialMetrics: this.spatialTelemetry.createInitialMetrics(players),
      events: [],
      recentEvents: [],
      snapshots: [],
      decisionLogs: [],
      lastSnapshotEventIndex: 0,
    };
    this.tactical.updateMovementIntent(this.state);
    this.playProgress = {
      position: { ...this.state.ball.position },
      ownerId: this.state.ball.ownerPlayerId,
      ballState: this.state.ball.state,
      at: this.state.time,
    };
    if (this.recordSnapshots) this.recordSnapshot();
  }

  run(): EngineMatchResult {
    return this.advanceTo(this.state.config.durationSeconds);
  }

  advanceTo(targetSecond: number): EngineMatchResult {
    const target = Math.max(this.state.time, Math.min(this.state.config.durationSeconds, targetSecond));
    if (!this.started) {
      this.started = true;
      this.decisionTick();
    }
    while (this.state.time + this.state.config.movementStepSeconds <= target + 1e-9) {
      this.advanceFixedStep(Math.min(
        this.state.config.movementStepSeconds,
        this.state.config.durationSeconds - this.state.time,
      ));
    }
    if (target >= this.state.config.durationSeconds && this.state.time + 1e-9 < this.state.config.durationSeconds) {
      this.advanceFixedStep(this.state.config.durationSeconds - this.state.time);
    }
    // The final whistle is also a hard upper bound for effects whose natural
    // expiry was clamped to match duration. It may fall between decision ticks.
    if (target >= this.state.config.durationSeconds) {
      const eventCount = this.state.events.length;
      this.fury.update(this.state, []);
      if (this.recordSnapshots && this.state.events.length > eventCount
        && this.state.snapshots.at(-1)?.second === this.state.config.durationSeconds) {
        this.state.snapshots.pop();
      }
    }
    if (this.recordSnapshots && target >= this.state.config.durationSeconds
      && this.state.snapshots.at(-1)?.second !== this.state.config.durationSeconds) this.recordSnapshot();
    return this.result();
  }

  activateFury(side: 0 | 1): EngineMatchEvent | undefined {
    const event = this.fury.activateManually(this.state, side);
    if (event && this.recordSnapshots) this.recordSnapshot();
    return event;
  }

  /**
   * Reuses the outgoing tactical slot instead of adding a thirteenth player to
   * the pitch. The runtime id remains stable for movement and ball references;
   * the catalog identity and the football profile are what change.
   */
  substitute(side: 0 | 1, outgoingPlayerId: string, incoming: EnginePlayer): EngineMatchEvent | undefined {
    if (this.state.time >= this.state.config.durationSeconds) return undefined;
    const runtimeIndex = this.state.players.findIndex((player) => player.teamSide === side && player.playerId === outgoingPlayerId);
    if (runtimeIndex < 0) return undefined;
    const outgoing = this.state.players[runtimeIndex];
    const sourceIncomingId = incoming.sourcePlayerId ?? incoming.id;
    if ((outgoing.sourcePlayerId ?? outgoing.playerId) === sourceIncomingId) return undefined;

    const team = this.state.teams[side];
    const slotIndex = team.players.findIndex((player) => player.id === outgoingPlayerId);
    if (slotIndex < 0) return undefined;

    const inheritedRole = outgoing.tacticalProfile.assignedRole;
    const assignedRole = validateAssignedRole(incoming.position, inheritedRole)
      ? inheritedRole
      : normaliseAssignedRole(incoming.position, incoming.tacticalProfile.assignedRole);
    const incomingInSlot: EnginePlayer = {
      ...incoming,
      id: outgoingPlayerId,
      sourcePlayerId: sourceIncomingId,
      tacticalProfile: { ...incoming.tacticalProfile, assignedRole },
    };
    const replacement = this.createRuntimePlayer(team, incomingInSlot, slotIndex, outgoing.role);
    this.spatialTelemetry.closeMetric(this.state, outgoing);
    replacement.spatialMetricId = `${outgoingPlayerId}:sub:${sourceIncomingId}:${this.state.time.toFixed(3)}`;
    // A substitution happens during a stoppage, so the new player inherits the
    // exact slot on the grass while entering with fresh stamina and TP.
    replacement.position = { ...outgoing.position };
    replacement.targetPosition = { ...outgoing.targetPosition };
    replacement.contextualTarget = { ...outgoing.contextualTarget };
    replacement.tacticalTarget = { ...outgoing.tacticalTarget };
    replacement.recoveryTarget = { ...outgoing.recoveryTarget };
    replacement.homePosition = { ...outgoing.homePosition };
    replacement.anchorPosition = { ...outgoing.anchorPosition };
    replacement.role = outgoing.role;
    replacement.hasBall = outgoing.hasBall;
    if (this.state.pendingAction?.actorId === outgoing.playerId) this.state.pendingAction = undefined;
    this.state.players[runtimeIndex] = replacement;
    this.state.spatialMetrics.push(this.spatialTelemetry.createMetric(replacement, this.state.time));
    team.players[slotIndex] = incomingInSlot;

    const event: EngineMatchEvent = {
      sequence: this.state.events.length,
      second: Number(this.state.time.toFixed(3)),
      type: 'SUBSTITUTION',
      team: side,
      actorId: outgoingPlayerId,
      actorSourcePlayerId: outgoing.sourcePlayerId ?? outgoing.playerId,
      actorAlias: outgoing.alias,
      targetId: outgoingPlayerId,
      targetSourcePlayerId: sourceIncomingId,
      targetAlias: replacement.alias,
      position: { ...outgoing.position },
      success: true,
      message: `${outgoing.fullName} deja el campo. Entra ${replacement.fullName}`,
      homeScore: this.state.score[0],
      awayScore: this.state.score[1],
      action: 'HOLD_POSITION',
    };
    this.state.events.push(event);
    this.state.recentEvents.push(event);
    if (this.state.recentEvents.length > 32) this.state.recentEvents.splice(0, this.state.recentEvents.length - 32);
    this.tactical.updateMovementIntent(this.state);
    if (this.recordSnapshots) this.recordSnapshot();
    return event;
  }

  getResult(): EngineMatchResult {
    return this.result();
  }

  private result(): EngineMatchResult {
    return {
      simulationVersion: SIMULATION_VERSION,
      state: this.state,
      homeScore: this.state.score[0],
      awayScore: this.state.score[1],
      events: this.state.events,
      snapshots: this.state.snapshots,
    };
  }

  private advanceFixedStep(deltaTime: number): void {
    if (deltaTime <= 0) return;
    this.movementTick(deltaTime);
    this.state.time = Number((this.state.time + deltaTime).toFixed(9));
    this.regenerateTechniquePoints(deltaTime);
    this.clock.advance(deltaTime);
    this.resolver.update(this.state, this.rng);
    this.recoverStalledPlay();
    if (this.state.kickoffTacticalRefresh) {
      this.state.kickoffTacticalRefresh = false;
      this.tactical.updateMovementIntent(this.state);
    }
    this.state.stats.possessionSeconds[this.state.possessionTeam] += deltaTime;
    if (this.clock.consumeDecision()) this.decisionTick();
    if (this.clock.consumeSnapshot() && this.recordSnapshots) this.recordSnapshot();
  }

  /**
   * No real phase should leave the ball and its owner effectively unchanged
   * for this long. Prepared passes and flights are exempt; their visible
   * motion is already in progress. The resolver creates a real safe pass or
   * a nearby loose-ball recovery, never a hidden teleport.
   */
  private recoverStalledPlay(): void {
    const ball = this.state.ball;
    const changedOwner = ball.ownerPlayerId !== this.playProgress.ownerId;
    const moved = distance(ball.position, this.playProgress.position) >= 2.5;
    const changedPhase = ball.state !== this.playProgress.ballState;
    if (changedOwner || moved || changedPhase) {
      this.playProgress = { position: { ...ball.position }, ownerId: ball.ownerPlayerId, ballState: ball.state, at: this.state.time };
      return;
    }
    if (this.state.pendingAction || ball.state === 'PASSING' || ball.state === 'SHOOTING'
      || this.state.time - this.playProgress.at < 7.5) return;
    this.resolver.resolveStalemate(this.state, this.rng);
    this.playProgress = {
      position: { ...this.state.ball.position }, ownerId: this.state.ball.ownerPlayerId,
      ballState: this.state.ball.state, at: this.state.time,
    };
    this.tactical.updateMovementIntent(this.state);
  }

  /** Restores TP continuously, which totals the configured amount each match minute.
   * This avoids a timing advantage at the tick just before/after a whole minute. */
  private regenerateTechniquePoints(deltaTime: number): void {
    const restored = this.state.config.tpRegenerationPerMinute * (deltaTime / 60);
    if (!Number.isFinite(restored) || restored <= 0) return;
    for (const player of this.state.players) {
      player.tpCurrent = Math.min(player.tpMax, player.tpCurrent + restored);
    }
  }

  movementTick(deltaTime: number): void {
    this.movement.update(this.state, deltaTime);
    this.spatialTelemetryAccumulator += deltaTime;
    if (this.spatialTelemetryAccumulator + 1e-9 >= this.spatialTelemetryIntervalSeconds
      || this.state.time + deltaTime + 1e-9 >= this.state.config.durationSeconds) {
      this.spatialTelemetry.update(this.state, this.spatialTelemetryAccumulator);
      this.spatialTelemetryAccumulator = 0;
    }
  }

  decisionTick(): void {
    this.fury.update(this.state, this.autoFurySides);
    this.tactical.updateMovementIntent(this.state);
    const owner = this.state.ball.ownerPlayerId
      ? this.state.players.find((player) => player.playerId === this.state.ball.ownerPlayerId)
      : undefined;
    if (!owner) return;

    const tackler = this.state.players
      .filter((player) => player.teamSide !== owner.teamSide && player.positionType !== 'GK' && player.actionCooldown <= 0)
      .sort((a, b) => distance(a.position, owner.position) - distance(b.position, owner.position) || a.playerId.localeCompare(b.playerId))[0];
    if (tackler
      && distance(tackler.position, owner.position) <= 2.2
      && this.state.time - this.state.lastTackleTime[tackler.teamSide] >= this.state.config.minimumTeamTackleIntervalSeconds) {
      const defensiveDecision = this.decisions.decide(this.state, tackler, this.rng);
      this.logger.record(defensiveDecision.context, defensiveDecision.candidates, defensiveDecision.chosen, defensiveDecision.selection);
      this.resolver.execute(this.state, defensiveDecision.chosen, this.rng, { prepareTechniques: true });
    }

    if (this.state.ball.ownerPlayerId !== owner.playerId || owner.actionCooldown > 0) return;
    const decision = this.decisions.decide(this.state, owner, this.rng);
    this.logger.record(decision.context, decision.candidates, decision.chosen, decision.selection);
    this.resolver.execute(this.state, decision.chosen, this.rng, { prepareTechniques: true });
    owner.lastDecisionTime = this.state.time;
  }

  getSnapshot(): EngineMatchSnapshot {
    return this.createSnapshot();
  }

  private createRuntimePlayers(team: EngineTeam): PlayerRuntimeState[] {
    return team.players.map((player, index) => this.createRuntimePlayer(team, player, index));
  }

  private createRuntimePlayer(team: EngineTeam, player: EnginePlayer, index: number, roleOverride?: string): PlayerRuntimeState {
    const rawHome = team.formationPositions[index] ?? { x: 50, y: 50 };
    // Formation coordinates describe a player's own left/right. When a team
    // attacks in the opposite direction, both pitch axes must be reflected:
    // only reflecting X puts its RB in the visual lane of the LB (and vice
    // versa) for an away side.
    const home: Vector2 = {
      x: PITCH_GEOMETRY.worldX(team.side, rawHome.x),
      y: team.side === 0 ? rawHome.y : PITCH_GEOMETRY.width - rawHome.y,
    };
    const direction: Vector2 = { x: team.side === 0 ? 1 : -1, y: 0 };
    return {
      playerId: player.id,
      sourcePlayerId: player.sourcePlayerId ?? player.id,
      spatialMetricId: player.id,
      teamId: team.id,
      teamSide: team.side,
      fullName: player.fullName,
      alias: player.alias,
      positionType: player.position,
      element: player.element,
      passive: player.passive,
      tacticalProfile: {
        ...player.tacticalProfile,
        assignedRole: normaliseAssignedRole(player.position, player.tacticalProfile.assignedRole),
      },
      stats: { ...player.stats }, baseStats: { ...player.stats },
      techniques: player.equippedTechniques.map((id) => this.techniques.get(id)).filter((item): item is EngineTechnique => Boolean(item)),
      position: { ...home }, velocity: { x: 0, y: 0 }, targetPosition: { ...home },
      tacticalTarget: { ...home }, contextualTarget: { ...home }, recoveryTarget: { ...home }, facingDirection: direction,
      reactionSecondsRemaining: 0,
      currentAction: 'HOLD_POSITION', movementState: player.position === 'GK' ? 'GOALKEEPER_POSITIONING' : 'HOLDING_SHAPE',
      intent: {
        movementState: player.position === 'GK' ? 'GOALKEEPER_POSITIONING' : 'HOLDING_SHAPE',
        reason: 'KEEP_SHAPE', idealTarget: { ...home }, reachableTarget: { ...home }, createdAt: 0,
      },
      actionCooldown: 0, staminaCurrent: 100, tpCurrent: player.tpMax, tpMax: player.tpMax,
      hasBall: false, role: roleOverride ?? team.roles[index] ?? player.position, homePosition: home,
      anchorPosition: { ...home }, preferredAnchorRadius: this.anchors.preferredRadius(player.position), currentZone: 'MIDDLE',
      lastDecisionTime: -1, lastTechniqueTime: -Infinity,
    };
  }

  private recordSnapshot(): void {
    const snapshot = this.createSnapshot();
    this.state.snapshots.push(snapshot);
    this.state.lastSnapshotEventIndex = this.state.events.length;
  }

  private createSnapshot(): EngineMatchSnapshot {
    const latestByActor = new Map<string, (typeof this.state.decisionLogs)[number]>();
    for (const entry of this.state.decisionLogs.slice(-8)) {
      latestByActor.set(entry.actorId, entry);
    }
    return {
      second: Number(this.state.time.toFixed(3)),
      players: this.state.players.map((player) => ({
        id: player.playerId,
        sourcePlayerId: player.sourcePlayerId ?? player.playerId,
        team: player.teamSide,
        x: player.position.x,
        y: player.position.y,
        role: player.role,
        element: player.element,
        facingDirection: { ...player.facingDirection },
        reactionSecondsRemaining: player.reactionSecondsRemaining,
        currentAction: player.currentAction,
        movementState: player.movementState,
        intentReason: player.intent.reason,
        intentExpiresAt: player.intent.expiresAt,
        anchorPosition: { ...player.anchorPosition },
        idealTarget: { ...player.intent.idealTarget },
        reachableTarget: { ...player.intent.reachableTarget },
        hasBall: player.hasBall,
      })),
      ball: {
        x: this.state.ball.position.x,
        y: this.state.ball.position.y,
        state: this.state.ball.state,
        velocity: { ...this.state.ball.velocity },
        height: this.state.ball.height,
        targetPlayerId: this.state.ball.targetPlayerId,
        targetSourcePlayerId: this.state.ball.targetPlayerId
          ? this.state.players.find((player) => player.playerId === this.state.ball.targetPlayerId)?.sourcePlayerId
          : undefined,
        passType: this.state.ball.flight?.passType,
      },
      possession: this.state.possessionTeam,
      teamPhases: [this.state.teamAi[0].phase, this.state.teamAi[1].phase],
      debug: {
        decisions: [...latestByActor.values()].map((entry) => {
          const actor = this.state.players.find((player) => player.playerId === entry.actorId);
          const opponent = entry.nearestOpponentId
            ? this.state.players.find((player) => player.playerId === entry.nearestOpponentId)
            : undefined;
          return {
            second: entry.minute * 60,
            actorId: entry.actorId,
            actorSourcePlayerId: actor?.sourcePlayerId ?? entry.actorId,
            team: actor?.teamSide ?? 0,
            position: { ...entry.position },
            pressure: entry.pressureLevel,
            nearestOpponentDistance: entry.nearestOpponentDistance,
            nearestOpponentId: entry.nearestOpponentId,
            nearestOpponentSourcePlayerId: opponent?.sourcePlayerId,
            localSpace: entry.localSpace,
            goalAngleQuality: entry.goalAngleQuality,
            chosenAction: entry.chosenAction,
            targetPosition: entry.targetPosition ? { ...entry.targetPosition } : undefined,
            scores: entry.utilityScores.slice(0, 5).map((score) => ({
              type: score.type,
              score: score.score,
              probability: score.selectionProbability,
              chosen: score.type === entry.chosenAction
                && score.targetPlayerId === entry.targetPlayerId
                && score.techniqueId === entry.techniqueId,
              passType: score.passType,
            })),
          };
        }),
      },
      fury: {
        active: [this.state.fury[0].active, this.state.fury[1].active],
        used: [this.state.fury[0].used, this.state.fury[1].used],
        remainingSeconds: [0, 1].map((side) => {
          const fury = this.state.fury[side as 0 | 1];
          return fury.active ? Math.max(0, (fury.endsAt ?? this.state.time) - this.state.time) : 0;
        }) as [number, number],
      },
      eventsSinceLastSnapshot: this.state.events.slice(this.state.lastSnapshotEventIndex),
    };
  }
}
